import React, { useMemo } from "react";
import { useQuery } from '@apollo/client/react';
import '../Settings.scss';
import { COUNTRY_COUNTS } from '../../../services/queries.js';
import { COUNTRIES_BY_REGION } from '../../../config/regionCountryDictionary.js';
import DataTable from './DataTable.jsx';

const REGION_COLS = [
    { label: 'ISO',     key: 'iso',   className: 'drilldown-col-iso',
      fmt: v => <code className="drilldown-code">{v}</code> },
    { label: 'Country', key: 'label' },
    { label: 'Count',   key: 'count', fmt: v => (v || 0).toLocaleString() },
];

const ORPHAN_COLS = [
    { label: 'ISO',   key: 'iso',   className: 'drilldown-col-iso',
      fmt: v => <code className="drilldown-code">{v}</code> },
    { label: 'Count', key: 'count', fmt: v => v.toLocaleString() },
];

const CountryCounts = () => {
    const { data, loading, error } = useQuery(COUNTRY_COUNTS, {
        fetchPolicy: 'cache-and-network',
    });

    const countryCountsMap = useMemo(() => {
        if (!data?.countByCountry) return {};

        return data.countByCountry.reduce((acc, item) => {
            acc[item.country] = item.count;
            return acc;
        }, {});
    }, [data]);

    const orphanCountries = useMemo(() => {
        if (!data?.countByCountry) return [];

        const regionIsos = new Set();
        Object.values(COUNTRIES_BY_REGION).forEach(region =>
            Object.values(region).forEach(country => {
                if (country.iso) regionIsos.add(country.iso);
            })
        );

        return data.countByCountry
            .filter(item => !regionIsos.has(item.country))
            .map(item => ({ iso: item.country, count: item.count }));
    }, [data]);

    return (
        <div className="country-counts">
            <div className="settings-section">
                <h3>Region-Country Dictionary</h3>
                <p>Countries organized by geographical/economic regions</p>

                {loading && <div className="settings-loading">Loading...</div>}
                {error && <div className="error">{error.message}</div>}

                {Object.entries(COUNTRIES_BY_REGION).map(([region, countries]) => (
                    <div key={region} style={{ marginBottom: '2rem' }}>
                        <h4 style={{ color: '#495057', marginBottom: '0.5rem' }}>{region}</h4>
                        <DataTable
                            cols={REGION_COLS}
                            rows={Object.values(countries).map(c => ({
                                iso: c.iso,
                                label: c.label,
                                count: countryCountsMap[c.iso] || 0,
                            }))}
                            rowKey="iso"
                        />
                    </div>
                ))}

                {orphanCountries.length > 0 && (
                    <div style={{ marginTop: '2rem' }}>
                        <h4>Countries Not in Region Dictionary</h4>
                        <DataTable
                            cols={ORPHAN_COLS}
                            rows={orphanCountries}
                            rowKey="iso"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default CountryCounts;
